import type { ProfessionAdminRow } from "@/services/platformProfessionsService";

/** Mirrors CreateProfessionDto: 16 is quote_fee_rate.discipline's width. */
export const SLUG_REGEX = /^[a-z][a-z0-9_]{1,15}$/;

/**
 * The strategy whose configuration is a register family plus its PrfIds.
 *
 * The ONE strategy name this screen knows, and it names a VERIFICATION rather
 * than a profession — the distinction decision a78bb4f turns on. Two
 * professions sharing a register share this branch; none of them is named.
 */
export const REGISTER_BACKED_STRATEGY = "poder_judicial";

/** PrfTpoPrf. The register answers CodError 1 for anything else. */
export const REGISTER_TYPE_CODES = ["A", "E", "P", "T"];

export interface CredentialDraft {
  prfId: string;
  attributeValue: string;
}

export interface FormState {
  slug: string;
  displayName: string;
  nounLabel: string;
  nounInSentence: string;
  consumerHint: string;
  scopeExamples: string;
  iconKey: string;
  verificationStrategies: string[];
  registerTypeCode: string;
  credentials: CredentialDraft[];
  sortOrder: string;
  isActive: boolean;
  triageEligible: boolean;
}

export const emptyForm = (strategy: string): FormState => ({
  slug: "",
  displayName: "",
  nounLabel: "",
  nounInSentence: "",
  consumerHint: "",
  scopeExamples: "",
  iconKey: "",
  verificationStrategies: [strategy],
  registerTypeCode: "",
  credentials: [],
  sortOrder: "100",
  isActive: true,
  /*
    NEVER defaulted on. tramite_knowledge is per-discipline and is what grounds
    both classification paths, so a profession with no corpus that is marked
    routable looks fully configured and silently receives nothing.
  */
  triageEligible: false,
});

export const formFrom = (row: ProfessionAdminRow): FormState => ({
  slug: row.slug,
  displayName: row.displayName,
  nounLabel: row.nounLabel ?? "",
  nounInSentence: row.nounInSentence ?? "",
  consumerHint: row.consumerHint ?? "",
  scopeExamples: row.scopeExamples ?? "",
  iconKey: row.iconKey ?? "",
  verificationStrategies: row.verificationStrategies ?? [],
  registerTypeCode: row.registerTypeCode ?? "",
  credentials: (row.credentials ?? []).map((c) => ({
    prfId: String(c.prfId),
    attributeValue: c.attributeValue ?? "",
  })),
  sortOrder: String(row.sortOrder),
  isActive: row.isActive,
  triageEligible: row.triageEligible,
});

/** Whether the form is complete enough to send. The server still decides. */
export function canSubmit(form: FormState, isEditing: boolean): boolean {
  const registerBacked = form.verificationStrategies.includes(REGISTER_BACKED_STRATEGY);
  if (!isEditing && !SLUG_REGEX.test(form.slug)) return false;
  if (form.displayName.trim().length < 2) return false;
  if (form.nounLabel.trim().length < 2) return false;
  if (form.nounInSentence.trim().length < 2) return false;
  // An empty set would admit every applicant unverified; the server refuses
  // it too, and this stops the admin reaching that refusal.
  if (form.verificationStrategies.length === 0) return false;
  if (registerBacked && !form.registerTypeCode) return false;
  if (!isEditing && registerBacked && !form.credentials.some((c) => c.prfId.trim())) return false;
  return true;
}
